import { useState, useMemo } from "react";
import axios from "axios";
import { Button } from "components/button";
import Table, { useTable } from "components/table";
import { useQuery, useQueryClient } from "react-query";
import { useModalContext } from "components/modal";
import { RiAddLine } from "react-icons/ri";
import { useTranslation } from 'react-i18next';
import PlanModal from "./components/PlanModal";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

export default function ServicePlans() {
  const { t } = useTranslation();
  const tBase = "views.admin.service_plans.index";
  const tr = (key) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();
  const queryClient = useQueryClient();
  
  const activeTable = useTable({
    defaultSort: { column: "id", order: "DESC" }
  });

  const archivedTable = useTable({
    defaultSort: { column: "id", order: "DESC" }
  });

  const [showArchived, setShowArchived] = useState(true);

  const plansData = useQuery(
    "plans",
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin?type=PLANS`
        )
        .then((res) => res.data)
  );
  
  const displayData = plansData?.data;

  // Helper function to sort data
  const sortData = (data, sortState) => {
    if (!data || !sortState.column) return data;

    return [...data].sort((a, b) => {
      const { column, order } = sortState;
      let aValue = a[column];
      let bValue = b[column];

      if (column === 'id') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.trim().toLowerCase();
        bValue = bValue.trim().toLowerCase();
      }

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'ASC' ? aValue - bValue : bValue - aValue;
      }

      // Handle strings and other types
      if (aValue < bValue) return order === 'ASC' ? -1 : 1;
      if (aValue > bValue) return order === 'ASC' ? 1 : -1;
      return 0;
    });
  };

  // Separate sorted data for each table
  const activePlans = useMemo(() => {
    const activePlans = displayData?.filter((plan) => !plan.archived);
    return sortData(activePlans, activeTable.sort);
  }, [displayData, activeTable.sort]);

  const archivedPlans = useMemo(() => {
    const archivedPlans = displayData?.filter((plan) => plan.archived);
    return sortData(archivedPlans, archivedTable.sort);
  }, [displayData, archivedTable.sort]);
  
  const appointmentSummary = (row) => {
    let plan;

    plansData?.data.forEach((x) => {
      if (x.id === row.id) {
        plan = x;
      }
    });

    let appSum = plan.appointment_types.map((i) => {
      return " " + i.label + "(" + i.service_plans_toapptypes.quantity + ")";
    });
    return appSum.toString();
  };

  const columns = {
    id: {
      label: "#",
      isClickable: false,
    },
    name: {
      label: tr("Name"),
      isClickable: false,
    },
    description: {
      label: tr("Description"),
      isClickable: false,
      cell: (row) => (
        <div className="max-w-[300px]" title={row.description}>
          <div className="truncate">{row.description}</div>
        </div>
      ),
    },
    duration: {
      label: "",
      labelDiv: (
        <div className="text-center">
          <div>{tr("Duration")}</div>
          <div className="text-xs text-gray-500">{tr("(Months)")}</div>
        </div>
      ),
      cell: (row) => (
        <div className="text-center">
          <div>{row.duration}</div>
        </div>
      ),
      isClickable: false,
    },
    appointments: {
      disabled: true,
      label: tr("active appointments"),
      isClickable: false,
      cell: (row) => (
        <div className="flex items-center italic">
        <div className="truncate">
          {appointmentSummary(row)}
        </div>
        </div>
      ),
    },
  };

  return (
    <div className="p-4 overflow-hidden"
      onFocus={() => {
        queryClient.invalidateQueries();
      }}
      onBlur={() => {
        queryClient.invalidateQueries();
      }}
    >
      <div className="text-left mb-4 flex flex-row flex-wrap items-center">
        <h1>{tr("Service Plans")}</h1>
        <Button
          primary
          style={{ marginLeft: "auto" }}
          onClick={() => {
            setModal({
              component: <PlanModal planId={"new"}></PlanModal>,
              label: tr("Add plan"),
              props: {
                shouldCloseOnOverlayClick: true,
                onAfterClose: () => {
                  queryClient.invalidateQueries();
                },
              },
            });
          }}
        >
          <RiAddLine /> {tr("Add New Plan")}
        </Button>
      </div>

      <Table
        handleClick={(row) => {
          setModal({
            component: (
              <PlanModal
                planId={row.id}
                data={plansData?.data.filter((x) => x.id === row.id)[0]}
              />
            ),
            label: tr("Edit plan ") + "#" + row.id,
            props: {
              shouldCloseOnOverlayClick: true,
              onAfterClose: () => {
                queryClient.invalidateQueries();
              },
            },
          });
        }}
        columns={columns}
        data={activePlans}
        sort={activeTable.sort}
        handleSort={activeTable.handleSort}
      />
      <div className="flex items-center space-x-2 mt-6">
        <h1>{tr("Archived")}</h1>
        <button
          onClick={() => setShowArchived(!showArchived)}
          className="flex items-center"
        >
          {showArchived 
            ? <IoIosArrowDown size={20} className="hover:text-primary" /> 
            : <IoIosArrowUp size={20} className="hover:text-primary" />
          }
        </button>
      </div>
      {showArchived && (
        <Table
          handleClick={(row) => {
            setModal({
              component: (
                <PlanModal
                  planId={row.id}
                  data={plansData?.data.filter((x) => x.id === row.id)[0]}
                />
              ),
              label: tr("Edit plan ") + "#" + row.id,
              props: {
                shouldCloseOnOverlayClick: true,
                onAfterClose: () => {
                  queryClient.invalidateQueries();
                },
              },
            });
          }}
          columns={columns}
          data={archivedPlans}
          sort={archivedTable.sort}
          handleSort={archivedTable.handleSort}
        />
      )}
    </div>
  );
}
